import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import Navbar from "../Dashboard/Navbar";
import Sidebar from "../Dashboard/Sidebar";
import { API_ENDPOINTS, API_URL } from "../../config";

// Add axios interceptor to ensure Authorization header is set for all requests
axios.interceptors.request.use(
  (config) => {
    const token = Cookies.get("access_token");
    if (token) {
      // Ensure token has Bearer prefix
      const authToken = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      config.headers.Authorization = authToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// TimeSlot component for available timings
const TimeSlot = ({ time, isSelected, onSelect }) => {
  return (
    <button
      type="button"
      className={`py-1 px-3 text-sm rounded-md ${
        isSelected
          ? "bg-blue-100 text-blue-700 border border-blue-500"
          : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
      }`}
      onClick={() => onSelect(time)}
    >
      {time}
    </button>
  );
};

// Input field component
const InputField = ({
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  disabled = false,
  isTextarea = false,
  rows = 4,
  children,
}) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {isTextarea ? (
        <textarea
          className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          rows={rows}
        />
      ) : children ? (
        children
      ) : (
        <input
          type={type}
          className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
        />
      )}
    </div>
  );
};

// Settings Section component
const SettingsSection = ({ title, children }) => {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
};

// Main Settings component
const Settings = () => {
  const navigate = useNavigate();
  const [userConfig, setUserConfig] = useState({
    name: "",
    timezone: "",
    background: "",
    preferred_meeting_schedule: "",
    rewrite_preferences: "",
    triage_no: "",
    triage_notify: "",
    triage_email: "",
    vip_list: [],
    email_fetch_days: 1,
    preferred_times: [], // For the time slots
    schedule_from: 9,
    schedule_to: 17,
    schedule_duration: 30,
    phone_number: "", // Added for phone number
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Available time slots
  const timeSlots = useMemo(
    () => [
      "06:00 AM",
      "07:00 AM",
      "08:00 AM",
      "09:00 AM",
      "10:00 AM",
      "11:00 PM",
      "12:00 PM",
      "01:00 PM",
      "02:00 PM",
      "03:00 PM",
      "04:00 PM",
      "05:00 PM",
      "06:00 PM",
      "07:00 PM",
    ],
    []
  );

  // Fetch config on initial load
  useEffect(() => {
    const fetchUserConfig = async () => {
      try {
        const token = Cookies.get("access_token");
        if (!token) {
          navigate("/");
          return;
        }

        setLoading(true);
        const configResponse = await axios.get(API_ENDPOINTS.GET_CONFIG);

        if (configResponse.data && configResponse.data.config) {
          const config = configResponse.data.config;
          console.log("User config response:", config);

          // Handle preferred times from schedule_preferences
          let preferredTimes = [];
          // This will be updated with any preferred times from the API when available

          // Extract schedule preferences
          const schedulePrefs = config.schedule_preferences || {};

          setUserConfig({
            name: config.name || "",
            timezone: config.timezone || "",
            background: config.background || "",
            preferred_meeting_schedule: config.background_preferences || "",
            rewrite_preferences: config.rewrite_preferences || "",
            triage_no: config.triage_no || "",
            triage_notify: config.triage_notify || "",
            triage_email: config.triage_email || "",
            vip_list: Array.isArray(config.vip_list) ? config.vip_list : [],
            email_fetch_days: config.email_fetch_days || 1,
            preferred_times: preferredTimes,
            schedule_from: schedulePrefs.start || 9,
            schedule_to: schedulePrefs.end || 17,
            schedule_duration: schedulePrefs.duration || 30,
            phone_number: configResponse.data.phone_number || "",
          });
        }
      } catch (configError) {
        console.error("Error fetching user config:", configError);
        if (configError.response?.status === 401) {
          navigate("/");
          return;
        }
        setError(
          "Failed to fetch user configuration. Please try refreshing the page."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserConfig();
  }, [navigate]);

  // Handle input changes
  const handleInputChange = useCallback((field, value) => {
    setUserConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Handle VIP list changes
  const handleAddVip = useCallback(() => {
    setUserConfig((prev) => ({
      ...prev,
      vip_list: [...(prev.vip_list || []), ""],
    }));
  }, []);

  const handleUpdateVip = useCallback((index, value) => {
    setUserConfig((prev) => {
      const newList = [...prev.vip_list];
      newList[index] = value;
      return {
        ...prev,
        vip_list: newList,
      };
    });
  }, []);

  const handleRemoveVip = useCallback((index) => {
    setUserConfig((prev) => {
      const newList = [...prev.vip_list];
      newList.splice(index, 1);
      return {
        ...prev,
        vip_list: newList,
      };
    });
  }, []);

  // Handle time slot selection
  const handleTimeSlotToggle = useCallback((timeSlot) => {
    setUserConfig((prev) => {
      const currentTimes = [...(prev.preferred_times || [])];
      if (currentTimes.includes(timeSlot)) {
        return {
          ...prev,
          preferred_times: currentTimes.filter((time) => time !== timeSlot),
        };
      } else {
        return {
          ...prev,
          preferred_times: [...currentTimes, timeSlot],
        };
      }
    });
  }, []);

  // Save changes
  const handleSave = async () => {
    try {
      setLoading(true);

      // Create the payload structure matching the API expectations
      const payloadToSend = {
        config: {
          name: userConfig.name,
          timezone: userConfig.timezone,
          background: userConfig.background,
          background_preferences: userConfig.preferred_meeting_schedule,
          rewrite_preferences: userConfig.rewrite_preferences,
          triage_no: userConfig.triage_no,
          triage_notify: userConfig.triage_notify,
          triage_email: userConfig.triage_email,
          vip_list: userConfig.vip_list,
          email_fetch_days: userConfig.email_fetch_days,
          schedule_preferences: {
            start: userConfig.schedule_from,
            end: userConfig.schedule_to,
            duration: userConfig.schedule_duration,
          },
        },
      };

      const response = await axios.patch(
        API_ENDPOINTS.UPDATE_CONFIG,
        payloadToSend
      );

      console.log("Update response:", response.data);

      if (response.data && response.data.config) {
        // Update with returned data
        const updatedConfig = response.data.config;

        // Extract schedule preferences
        const schedulePrefs = updatedConfig.schedule_preferences || {};
        let scheduleFrom = 9;
        let scheduleTo = 17;
        let scheduleDuration = 30;

        if (typeof schedulePrefs === "string") {
          try {
            const parsedPrefs = JSON.parse(schedulePrefs);
            scheduleFrom = parsedPrefs.start || 9;
            scheduleTo = parsedPrefs.end || 17;
            scheduleDuration = parsedPrefs.duration || 30;
          } catch (e) {
            console.warn(
              "Failed to parse schedule_preferences from response:",
              e
            );
          }
        } else if (typeof schedulePrefs === "object") {
          scheduleFrom = schedulePrefs.start || 9;
          scheduleTo = schedulePrefs.end || 17;
          scheduleDuration = schedulePrefs.duration || 30;
        }

        setUserConfig({
          name: updatedConfig.name || "",
          timezone: updatedConfig.timezone || "",
          background: updatedConfig.background || "",
          preferred_meeting_schedule:
            updatedConfig.background_preferences || "",
          rewrite_preferences: updatedConfig.rewrite_preferences || "",
          triage_no: updatedConfig.triage_no || "",
          triage_notify: updatedConfig.triage_notify || "",
          triage_email: updatedConfig.triage_email || "",
          vip_list: Array.isArray(updatedConfig.vip_list)
            ? updatedConfig.vip_list
            : [],
          email_fetch_days: updatedConfig.email_fetch_days || 1,
          preferred_times: userConfig.preferred_times,
          schedule_from: scheduleFrom,
          schedule_to: scheduleTo,
          schedule_duration: scheduleDuration,
        });

        setSuccessMessage("Settings updated successfully");
        setIsEditing(false);
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error("Error saving configuration:", error);
      setError("Failed to update settings. Please try again.");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle sidebar state changes
  const handleSidebarStateChange = (collapsed) => {
    setIsSidebarCollapsed(collapsed);
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          isSidebarCollapsed ? "w-[95%] ml-[5%]" : "w-[82%] ml-[18%]"
        } h-screen overflow-hidden`}
      >
        <Navbar />
        <div className="flex flex-1 overflow-y-auto h-full">
          <Sidebar onCollapse={handleSidebarStateChange} />
          <div className="flex-1 flex items-center justify-center min-h-[50vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex-1 flex flex-col transition-all duration-300 ${
        isSidebarCollapsed ? "w-[95%] ml-[5%]" : "w-[82%] ml-[18%]"
      } h-screen overflow-hidden`}
    >
      <Navbar isSidebarCollapsed={isSidebarCollapsed} />
      <div className="flex flex-1 overflow-y-auto h-full">
        <Sidebar onCollapse={handleSidebarStateChange} />
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-blue-500">
                  Settings
                </h2>
                {isEditing ? (
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none"
                  >
                    Save Changes
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none"
                  >
                    Edit
                  </button>
                )}
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-400 text-green-700">
                  {successMessage}
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                {/* Basic Info Section */}
                <SettingsSection title="Basic Info">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      label="Full Name"
                      placeholder="Enter your name..."
                      value={userConfig.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      disabled={!isEditing}
                    />
                    <InputField
                      label="Time zone"
                      placeholder="Set your time zone"
                      disabled={!isEditing}
                    >
                      <select
                        className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        value={userConfig.timezone || ""}
                        onChange={(e) =>
                          handleInputChange("timezone", e.target.value)
                        }
                        disabled={!isEditing}
                      >
                        <option value="">Select timezone</option>
                        <option value="Asia/Karachi">
                          Asia/Karachi (GMT+5)
                        </option>
                        <option value="Asia/Kolkata">
                          Asia/Kolkata (GMT+5:30)
                        </option>
                        <option value="Asia/Dubai">Asia/Dubai (GMT+4)</option>
                        <option value="Asia/Riyadh">Asia/Riyadh (GMT+3)</option>
                        <option value="Asia/Baghdad">
                          Asia/Baghdad (GMT+3)
                        </option>
                        <option value="Asia/Tehran">
                          Asia/Tehran (GMT+3:30)
                        </option>
                        <option value="Asia/Kabul">
                          Asia/Kabul (GMT+4:30)
                        </option>
                        <option value="Asia/Tashkent">
                          Asia/Tashkent (GMT+5)
                        </option>
                        <option value="Asia/Yekaterinburg">
                          Asia/Yekaterinburg (GMT+5)
                        </option>
                        <option value="Asia/Dhaka">Asia/Dhaka (GMT+6)</option>
                        <option value="Asia/Yangon">
                          Asia/Yangon (GMT+6:30)
                        </option>
                        <option value="Asia/Bangkok">
                          Asia/Bangkok (GMT+7)
                        </option>
                        <option value="Asia/Jakarta">
                          Asia/Jakarta (GMT+7)
                        </option>
                        <option value="Asia/Singapore">
                          Asia/Singapore (GMT+8)
                        </option>
                        <option value="Asia/Kuala_Lumpur">
                          Asia/Kuala_Lumpur (GMT+8)
                        </option>
                        <option value="Asia/Shanghai">
                          Asia/Shanghai (GMT+8)
                        </option>
                        <option value="Asia/Hong_Kong">
                          Asia/Hong_Kong (GMT+8)
                        </option>
                        <option value="Asia/Taipei">Asia/Taipei (GMT+8)</option>
                        <option value="Asia/Tokyo">Asia/Tokyo (GMT+9)</option>
                        <option value="Asia/Seoul">Asia/Seoul (GMT+9)</option>
                        <option value="Asia/Pyongyang">
                          Asia/Pyongyang (GMT+9)
                        </option>

                        <option value="Australia/Perth">
                          Australia/Perth (GMT+8)
                        </option>
                        <option value="Australia/Darwin">
                          Australia/Darwin (GMT+9:30)
                        </option>
                        <option value="Australia/Adelaide">
                          Australia/Adelaide (GMT+9:30)
                        </option>
                        <option value="Australia/Brisbane">
                          Australia/Brisbane (GMT+10)
                        </option>
                        <option value="Australia/Sydney">
                          Australia/Sydney (GMT+10)
                        </option>
                        <option value="Australia/Melbourne">
                          Australia/Melbourne (GMT+10)
                        </option>
                        <option value="Australia/Hobart">
                          Australia/Hobart (GMT+10)
                        </option>
                        <option value="Pacific/Auckland">
                          Pacific/Auckland (GMT+12)
                        </option>
                        <option value="Pacific/Fiji">
                          Pacific/Fiji (GMT+12)
                        </option>
                        <option value="Pacific/Honolulu">
                          Pacific/Honolulu (GMT-10)
                        </option>

                        <option value="America/New_York">
                          America/New_York (EST)
                        </option>
                        <option value="America/Chicago">
                          America/Chicago (CST)
                        </option>
                        <option value="America/Denver">
                          America/Denver (MST)
                        </option>
                        <option value="America/Los_Angeles">
                          America/Los_Angeles (PST)
                        </option>
                        <option value="America/Anchorage">
                          America/Anchorage (GMT-9)
                        </option>
                        <option value="America/Halifax">
                          America/Halifax (GMT-4)
                        </option>
                        <option value="America/St_Johns">
                          America/St_Johns (GMT-3:30)
                        </option>
                        <option value="America/Toronto">
                          America/Toronto (GMT-5)
                        </option>
                        <option value="America/Winnipeg">
                          America/Winnipeg (GMT-6)
                        </option>
                        <option value="America/Edmonton">
                          America/Edmonton (GMT-7)
                        </option>
                        <option value="America/Vancouver">
                          America/Vancouver (GMT-8)
                        </option>
                        <option value="America/Mexico_City">
                          America/Mexico_City (GMT-6)
                        </option>
                        <option value="America/Bogota">
                          America/Bogota (GMT-5)
                        </option>
                        <option value="America/Lima">
                          America/Lima (GMT-5)
                        </option>
                        <option value="America/Caracas">
                          America/Caracas (GMT-4)
                        </option>
                        <option value="America/Santiago">
                          America/Santiago (GMT-4)
                        </option>
                        <option value="America/Buenos_Aires">
                          America/Buenos_Aires (GMT-3)
                        </option>
                        <option value="America/Sao_Paulo">
                          America/Sao_Paulo (GMT-3)
                        </option>

                        <option value="Europe/London">
                          Europe/London (GMT)
                        </option>
                        <option value="Europe/Dublin">
                          Europe/Dublin (GMT)
                        </option>
                        <option value="Europe/Lisbon">
                          Europe/Lisbon (GMT)
                        </option>
                        <option value="Europe/Madrid">
                          Europe/Madrid (GMT+1)
                        </option>
                        <option value="Europe/Paris">
                          Europe/Paris (GMT+1)
                        </option>
                        <option value="Europe/Brussels">
                          Europe/Brussels (GMT+1)
                        </option>
                        <option value="Europe/Amsterdam">
                          Europe/Amsterdam (GMT+1)
                        </option>
                        <option value="Europe/Berlin">
                          Europe/Berlin (GMT+1)
                        </option>
                        <option value="Europe/Rome">Europe/Rome (GMT+1)</option>
                        <option value="Europe/Zurich">
                          Europe/Zurich (GMT+1)
                        </option>
                        <option value="Europe/Vienna">
                          Europe/Vienna (GMT+1)
                        </option>
                        <option value="Europe/Prague">
                          Europe/Prague (GMT+1)
                        </option>
                        <option value="Europe/Warsaw">
                          Europe/Warsaw (GMT+1)
                        </option>
                        <option value="Europe/Budapest">
                          Europe/Budapest (GMT+1)
                        </option>
                        <option value="Europe/Stockholm">
                          Europe/Stockholm (GMT+1)
                        </option>
                        <option value="Europe/Copenhagen">
                          Europe/Copenhagen (GMT+1)
                        </option>
                        <option value="Europe/Oslo">Europe/Oslo (GMT+1)</option>
                        <option value="Europe/Helsinki">
                          Europe/Helsinki (GMT+2)
                        </option>
                        <option value="Europe/Athens">
                          Europe/Athens (GMT+2)
                        </option>
                        <option value="Europe/Bucharest">
                          Europe/Bucharest (GMT+2)
                        </option>
                        <option value="Europe/Kiev">Europe/Kiev (GMT+2)</option>
                        <option value="Europe/Moscow">
                          Europe/Moscow (GMT+3)
                        </option>
                        <option value="Europe/Istanbul">
                          Europe/Istanbul (GMT+3)
                        </option>

                        <option value="Africa/Cairo">
                          Africa/Cairo (GMT+2)
                        </option>
                        <option value="Africa/Johannesburg">
                          Africa/Johannesburg (GMT+2)
                        </option>
                        <option value="Africa/Nairobi">
                          Africa/Nairobi (GMT+3)
                        </option>
                        <option value="Africa/Lagos">
                          Africa/Lagos (GMT+1)
                        </option>
                        <option value="Africa/Casablanca">
                          Africa/Casablanca (GMT)
                        </option>
                        <option value="Africa/Tunis">
                          Africa/Tunis (GMT+1)
                        </option>
                        <option value="Africa/Algiers">
                          Africa/Algiers (GMT+1)
                        </option>
                        <option value="Africa/Accra">Africa/Accra (GMT)</option>
                        <option value="Africa/Dakar">Africa/Dakar (GMT)</option>

                        <option value="Asia/Jerusalem">
                          Asia/Jerusalem (GMT+2)
                        </option>
                        <option value="Asia/Beirut">Asia/Beirut (GMT+2)</option>
                        <option value="Asia/Damascus">
                          Asia/Damascus (GMT+2)
                        </option>
                        <option value="Asia/Amman">Asia/Amman (GMT+2)</option>
                        <option value="Asia/Qatar">Asia/Qatar (GMT+3)</option>
                      </select>
                    </InputField>
                  </div>
                </SettingsSection>

                {/* Background Section */}
                <SettingsSection title="Background">
                  <InputField
                    label="Your Professional background and context"
                    placeholder="Write professional background to assist AI"
                    value={userConfig.background}
                    onChange={(e) =>
                      handleInputChange("background", e.target.value)
                    }
                    disabled={!isEditing}
                    isTextarea={true}
                    rows={4}
                  />
                </SettingsSection>

                {/* Schedule Preferences Section */}
                <SettingsSection title="Schedule Preferences">
                  {/* Schedule Time Range */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        value={userConfig.schedule_from}
                        onChange={(e) =>
                          handleInputChange(
                            "schedule_from",
                            parseInt(e.target.value)
                          )
                        }
                        disabled={!isEditing}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i}:00
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        value={userConfig.schedule_to}
                        onChange={(e) =>
                          handleInputChange(
                            "schedule_to",
                            parseInt(e.target.value)
                          )
                        }
                        disabled={!isEditing}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i}:00
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </SettingsSection>

                {/* Rewrite Preferences Section */}
                <SettingsSection title="Rewrite Preferences">
                  <InputField
                    label="How you want your emails to be rewritten"
                    placeholder="Write emails to be rewritten to assist AI"
                    value={userConfig.rewrite_preferences}
                    onChange={(e) =>
                      handleInputChange("rewrite_preferences", e.target.value)
                    }
                    disabled={!isEditing}
                    isTextarea={true}
                    rows={4}
                  />
                </SettingsSection>

                {/* Triage Sections */}
                <SettingsSection title="Triage - No Response Required">
                  <InputField
                    label="Emails that don't require a response"
                    placeholder="Write No Response Required to assist AI"
                    value={userConfig.triage_no}
                    onChange={(e) =>
                      handleInputChange("triage_no", e.target.value)
                    }
                    disabled={!isEditing}
                    isTextarea={true}
                    rows={4}
                  />
                </SettingsSection>

                <SettingsSection title="Triage - Notify">
                  <InputField
                    label="Emails that require notification"
                    placeholder="Write required emails to assist AI"
                    value={userConfig.triage_notify}
                    onChange={(e) =>
                      handleInputChange("triage_notify", e.target.value)
                    }
                    disabled={!isEditing}
                    isTextarea={true}
                    rows={4}
                  />
                </SettingsSection>

                <SettingsSection title="Triage - Email">
                  <InputField
                    label="Emails that require a response"
                    placeholder="Write required response emails to assist AI"
                    value={userConfig.triage_email}
                    onChange={(e) =>
                      handleInputChange("triage_email", e.target.value)
                    }
                    disabled={!isEditing}
                    isTextarea={true}
                    rows={4}
                  />
                </SettingsSection>

                {/* Email Fetch Days */}
                <SettingsSection title="Email Fetch Days">
                  <InputField
                    label="Fetch emails from how many past days (1–7)"
                    placeholder="1"
                    type="number"
                    value={userConfig.email_fetch_days}
                    onChange={(e) =>
                      handleInputChange(
                        "email_fetch_days",
                        Math.min(7, Math.max(1, parseInt(e.target.value) || 1))
                      )
                    }
                    disabled={!isEditing}
                  >
                    <select
                      className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                      value={userConfig.email_fetch_days || 1}
                      onChange={(e) =>
                        handleInputChange(
                          "email_fetch_days",
                          parseInt(e.target.value)
                        )
                      }
                      disabled={!isEditing}
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                    </select>
                  </InputField>
                </SettingsSection>

                {/* VIP List */}
                <SettingsSection title="VIP List">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email addresses that will receive priority treatment
                  </label>

                  {userConfig.vip_list && userConfig.vip_list.length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {userConfig.vip_list.map((email, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="email"
                            className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={email}
                            onChange={(e) =>
                              handleUpdateVip(index, e.target.value)
                            }
                            placeholder="Enter email address"
                            disabled={!isEditing}
                          />
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleRemoveVip(index)}
                              className="p-2 text-red-500 hover:text-red-700 focus:outline-none"
                              title="Remove"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 mb-3 p-2 bg-gray-50 rounded-md">
                      No VIP emails added
                    </div>
                  )}

                  {isEditing && (
                    <button
                      type="button"
                      onClick={handleAddVip}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Email
                    </button>
                  )}
                </SettingsSection>

                {/* Save Changes button at bottom */}
                {isEditing && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="submit"
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
